/**
 * <custom-options> web component.
 *
 * Moves the Liquid-rendered accordions from inside <custom-options> into
 * <variant-selects> so they become siblings of the native variant accordions.
 * From there <variant-selects>'s own change listener handles the cascade
 * (variant id, metavariant, image set) and updates the summary text from each
 * radio's <label title>. The component holds no state.
 *
 * One-time onFieldsUpdated() sync after the move; deferred until media-gallery
 * is ready because variantSelects.updateVariantImageSet() crashes on an
 * undefined mediaGallery if called synchronously at parse time.
 */
if (!customElements.get('custom-options')) {
  customElements.define('custom-options', class CustomOptions extends HTMLElement {
    connectedCallback() {
      const sectionId = this.dataset.sectionId;
      if (!sectionId) return;

      const variantSelects = document.querySelector(
        `variant-selects[id^="variant-selects-${sectionId}"]`
      );
      if (!variantSelects) return;

      const existingAccordionCount = variantSelects.querySelectorAll(
        ':scope > .product__accordion--variant'
      ).length;

      const ourAccordions = Array.from(
        this.querySelectorAll(':scope > [data-custom-options-accordion]')
      );

      ourAccordions.forEach((accordion, index) => {
        const chipEl = accordion.querySelector('[data-custom-options-chip]');
        if (chipEl) {
          chipEl.textContent = String(existingAccordionCount + 1 + index).padStart(2, '0');
        }
        variantSelects.appendChild(accordion);
      });

      this.applyUrlPreselect(variantSelects, ourAccordions);
      this.syncVariantSelects(variantSelects);
    }

    /**
     * Mirrors the &io=Lewa,Blok,Box URL-param preselection from
     * assets/product-infinite-options.js. Order-independent; unknown values
     * silently ignored. Per-field "first match wins" so duplicate option
     * values within a field don't cause surprises.
     *
     * Runs AFTER the move into <variant-selects> so updateSelectedSwatchValue
     * can locate the [data-selected-swatch-value] summary spans (it queries
     * variant-selects' subtree).
     */
    applyUrlPreselect(variantSelects, accordions) {
      const ioParams = new URLSearchParams(window.location.search).get('io')?.split(',');
      if (!ioParams) return;

      accordions.forEach((accordion) => {
        const radios = Array.from(accordion.querySelectorAll('input[type="radio"]'));
        const match = radios.find((r) => ioParams.includes(r.value));
        if (!match || match.checked) return;
        match.checked = true;
        if (typeof variantSelects.updateSelectedSwatchValue === 'function') {
          variantSelects.updateSelectedSwatchValue({ target: match });
        }
      });
    }

    syncVariantSelects(variantSelects) {
      if (typeof variantSelects.onFieldsUpdated !== 'function') return;

      if (variantSelects.mediaGallery) {
        variantSelects.onFieldsUpdated();
        return;
      }

      // Wait for media-gallery:ready so variantSelects.mediaGallery is assigned
      // before onFieldsUpdated runs. rAF lets variant-selects' own ready
      // handler land first.
      const onReady = () => {
        requestAnimationFrame(() => {
          if (variantSelects.mediaGallery) variantSelects.onFieldsUpdated();
        });
      };
      variantSelects.addEventListener('media-gallery:ready', onReady, { once: true });
      document.addEventListener('media-gallery:ready', onReady, { once: true });
    }
  });
}
